import { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { saveHeaderTitle } from "../../features/HomePage/HomePageSlice";
import { useVTOViewer } from "../VTOViewer/useVTOViewer";

const BROWSE_PAGE_SIZE = 10;

export const useVirtualTryOn = () => {
  const dispatch = useDispatch();

  const [activeGender, setActiveGender] = useState("all"); // "all", "men", or "women"
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [mode, setMode] = useState("side"); // "compare" or "side"
  const [sideCarouselState, setSideCarouselState] = useState({});

  const [zoomModal, setZoomModal] = useState({
    front: null,
    back: null,
    showFlip: false,
    isMobileOpen: false,
  });

  const {
    refMap,
    sectionsData: rawSectionsData,
    loading,
    error,
    activeSlideIndices,
    slideTo,
    initializeDragHandles,
    pagination,
  } = useVTOViewer({
    gender: activeGender,
    type: "on user",
    page: currentPage,
    limit: BROWSE_PAGE_SIZE,
    search: searchTerm,
  });

  const sectionsData = rawSectionsData.map((sec) => ({
    ...sec,
    title: "Virtual Try-On",
  }));

  const hasAnyLooks = sectionsData.some(
    (section) =>
      (!activeGender || activeGender === "all" || section.activeKey === activeGender) &&
      Array.isArray(section.slides) &&
      section.slides.length > 0
  );

  const isMobileViewport = () =>
    typeof window !== "undefined" && window.innerWidth < 768;

  const openZoomModal = (front, back = null, showFlip = false) => {
    setZoomModal({
      front,
      back,
      showFlip,
      isMobileOpen: isMobileViewport(),
    });
  };

  const closeZoomModal = () => {
    setZoomModal({
      front: null,
      back: null,
      showFlip: false,
      isMobileOpen: false,
    });
  };

  useEffect(() => {
    dispatch(saveHeaderTitle("Virtual Try-On"));
  }, [dispatch]);

  // Listen to keyboard arrow keys to navigate the active Swiper carousel
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (
        document.activeElement &&
        (document.activeElement.tagName === "INPUT" ||
          document.activeElement.tagName === "TEXTAREA" ||
          document.activeElement.isContentEditable)
      ) {
        return;
      }
      const zoomModalOpen = document
        .getElementById("modelZoomModal")
        ?.classList.contains("show");
      if (zoomModalOpen) return;

      const activeSwiper =
        activeGender === "women"
          ? refMap.productSwiperRef?.current?.swiper
          : refMap.modelSwiperRef?.current?.swiper;

      if (!activeSwiper) return;

      if (e.key === "ArrowRight") {
        e.preventDefault();
        activeSwiper.slideNext();
      } else if (e.key === "ArrowLeft") {
        e.preventDefault();
        activeSwiper.slidePrev();
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [activeGender, refMap]);

  // Re-run drag handle listener bindings whenever the mode toggles back to "compare"
  useEffect(() => {
    if (mode === "compare" && sectionsData.length > 0) {
      const timer = setTimeout(() => {
        initializeDragHandles();
      }, 100);
      return () => clearTimeout(timer);
    }
    return undefined;
  }, [mode, sectionsData, initializeDragHandles]);

  const resetToFirstPage = () => {
    setCurrentPage(1);
  };

  const handleGenderChange = (gender) => {
    setActiveGender((prev) => (prev === gender ? "all" : gender));
    resetToFirstPage();
  };

  const handleSearchChange = (event) => {
    setSearchTerm(event.target.value);
    resetToFirstPage();
  };

  const selectLook = (section, index) => {
    slideTo(section.activeKey, index);
  };

  const previousBrowsePage = () => {
    setCurrentPage((page) => Math.max(1, page - 1));
  };

  const nextBrowsePage = () => {
    setCurrentPage((page) => Math.min(pagination.totalPages || 1, page + 1));
  };

  const updateSideCarouselState = (key, element) => {
    if (!element) return;
    const maxScrollLeft = element.scrollWidth - element.clientWidth;
    setSideCarouselState((prev) => {
      const nextState = {
        canScrollLeft: element.scrollLeft > 4,
        canScrollRight: element.scrollLeft < maxScrollLeft - 4,
      };
      const currentState = prev[key];
      if (
        currentState?.canScrollLeft === nextState.canScrollLeft &&
        currentState?.canScrollRight === nextState.canScrollRight
      ) {
        return prev;
      }
      return { ...prev, [key]: nextState };
    });
  };

  const scrollSideCarousel = (event, key, direction) => {
    event.preventDefault();
    event.stopPropagation();
    const shell = event.currentTarget.closest("[data-side-carousel-shell]");
    const carousel = shell?.querySelector("[data-drag-box]");
    if (!carousel) return;
    carousel.scrollBy({
      left: direction * carousel.clientWidth,
      behavior: "smooth",
    });
    window.setTimeout(() => updateSideCarouselState(key, carousel), 250);
  };

  const activeModeClass =
    "px-2.5 py-1 lg:px-3.5 lg:py-1.5 rounded-md bg-primary text-white shadow-sm text-[11px] lg:text-xs font-bold transition-all cursor-pointer border-none";
  const inactiveModeClass =
    "px-2.5 py-1 lg:px-3.5 lg:py-1.5 rounded-md text-slate-500 hover:text-slate-900 text-[11px] lg:text-xs font-semibold transition-all cursor-pointer border-none bg-transparent";

  return {
    activeGender,
    handleGenderChange,
    searchTerm,
    handleSearchChange,
    currentPage,
    loading,
    error,
    hasAnyLooks,
    sectionsData,
    activeSlideIndices,
    pagination,
    mode,
    setMode,
    activeModeClass,
    inactiveModeClass,
    refMap,
    selectLook,
    previousBrowsePage,
    nextBrowsePage,
    sideCarouselState,
    scrollSideCarousel,
    updateSideCarouselState,
    zoomModal,
    openZoomModal,
    closeZoomModal,
  };
};

export default useVirtualTryOn;
